import {captureException} from '@sentry/browser';
import {showNotification} from 'platform/components';

// `fetch` rejects with `TypeError: Failed to fetch` for transient client-side
// failures (offline, CORS, blocked by extension/proxy, navigation cancel).
// These are not actionable bugs - the user already sees an error notification -
// so we keep them out of Sentry to avoid noise.
const isNetworkFetchError = (error: unknown) =>
  error instanceof TypeError && /failed to fetch|load failed|networkerror/i.test(error.message);

export const printPDF = (urlDataOrBlob: string | Blob) => {
  const uniqueIframeId = `printFrame_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  if (typeof urlDataOrBlob === 'string') {
    const url = new URL(urlDataOrBlob);
    const hasInlineTypeInUrl = url.searchParams.get('type') === 'inline';

    if (!hasInlineTypeInUrl) {
      url.searchParams.append('type', 'inline');
    }

    fetch(url)
      .then((res) => res.blob())
      .then((file) => {
        handlePDFBlob(file, uniqueIframeId);
      })
      .catch((error) => {
        console.error(error);
        showNotification.error();
        if (!isNetworkFetchError(error)) {
          captureException(error);
        }
      });
  } else {
    handlePDFBlob(urlDataOrBlob, uniqueIframeId);
  }
};

const handlePDFBlob = (file: Blob, iframeId: string) => {
  const fileUrl = URL.createObjectURL(file);
  const printFrame = document.createElement('iframe');

  printFrame.setAttribute('id', iframeId);
  printFrame.setAttribute('src', fileUrl);

  if (isFirefox()) {
    printFrame.setAttribute(
      'style',
      'width: 1px; height: 100px; position: fixed; left: 0; top: 0; opacity: 0; border-width: 0; margin: 0; padding: 0'
    );
  } else {
    // Hide the iframe in other browsers
    printFrame.setAttribute(
      'style',
      'visibility: hidden; height: 0; width: 0; position: absolute; border: 0'
    );
  }

  document.body.appendChild(printFrame);
  const iframeElement = document.getElementById(iframeId) as HTMLIFrameElement;

  iframeElement.onload = () => {
    // Add a delay for Firefox. In my tests, 1000ms was sufficient but 100ms was not
    if (isFirefox() && getFirefoxMajorVersion() < 110) {
      setTimeout(() => performPrint(iframeElement, fileUrl, iframeId), 1000);
    } else {
      performPrint(iframeElement, fileUrl, iframeId);
    }
  };
};

const performPrint = (iframeElement: HTMLIFrameElement, fileUrl: string, iframeId: string) => {
  try {
    iframeElement.focus();

    if (isEdge()) {
      try {
        iframeElement.contentWindow?.document.execCommand('print', false);
      } catch {
        setTimeout(() => {
          iframeElement.contentWindow?.print();
        }, 1000);
      }
    } else {
      setTimeout(() => {
        iframeElement.contentWindow?.print();
      }, 1000);
    }
  } catch (error) {
    showNotification.error();
    captureException(error);
    console.error(error);
  } finally {
    if (isFirefox() && getFirefoxMajorVersion() < 110) {
      iframeElement.style.visibility = 'hidden';
      iframeElement.style.left = '-1px';
    }
    cleanUp(fileUrl, iframeId);
  }
};

export function cleanUp(fileUrl: string, iframeId: string) {
  if (isSafari()) {
    setTimeout(() => {
      window.URL.revokeObjectURL(fileUrl);

      const iframe = document.getElementById(iframeId);
      const fileURL = iframe?.getAttribute('src');

      fileURL && window.URL.revokeObjectURL(fileURL);
      iframe &&
        setTimeout(() => {
          iframe?.remove?.();
        }, 250);
    }, 30000);
  } else {
    const event = isChrome() || isFirefox() ? 'focus' : 'mouseover';

    const handler = () => {
      window.removeEventListener(event, handler);
      window.URL.revokeObjectURL(fileUrl);

      const iframe = document.getElementById(iframeId);
      const fileURL = iframe?.getAttribute('src');

      fileURL && window.URL.revokeObjectURL(fileURL);
      iframe &&
        setTimeout(() => {
          iframe?.remove?.();
        }, 250);
    };

    window.addEventListener(event, handler);
  }
}

//@ts-ignore window.chrome is Chrome specific
const isChrome = () => !!window.chrome;

//@ts-ignore window.StyleMedia is Edge specific
const isEdge = () => !!window.StyleMedia;

//@ts-ignore InstallTrigger is firefox specific
const isFirefox = () => typeof InstallTrigger !== 'undefined';

const isSafari = () => {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Safari') && !userAgent.includes('Chrome');
};

const getFirefoxMajorVersion = () => {
  const userAgent = navigator.userAgent;
  const firefoxVersionRegex = /firefox\/(\S+)/;
  const match = userAgent.toLowerCase().match(firefoxVersionRegex);
  if (match) {
    //@ts-ignore
    return match[1].split('.').map((x) => parseInt(x))[0];
  }
  return 0;
};
